import { useEffect, useCallback, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center';

export interface ToastProps {
  /** Unique identifier */
  id: string;
  /** Toast message */
  message: string;
  /** Optional title */
  title?: string;
  /** Toast type/variant */
  type?: ToastType;
  /** Auto dismiss duration in ms (0 to disable) */
  duration?: number;
  /** Custom icon */
  icon?: ReactNode;
  /** Show close button */
  dismissible?: boolean;
  /** Callback when toast is dismissed */
  onDismiss?: (id: string) => void;
}

export interface ToastContainerProps {
  /** Toast position on screen */
  position?: ToastPosition;
  /** Array of toast items */
  toasts: ToastProps[];
  /** Callback to remove a toast */
  onDismiss: (id: string) => void;
}

const typeConfig: Record<
  ToastType,
  { icon: ReactNode; bgClass: string; borderClass: string; iconClass: string }
> = {
  success: {
    icon: <CheckCircle size={20} />,
    bgClass: 'bg-success/10',
    borderClass: 'border-success',
    iconClass: 'text-success',
  },
  error: {
    icon: <AlertCircle size={20} />,
    bgClass: 'bg-error/10',
    borderClass: 'border-error',
    iconClass: 'text-error',
  },
  warning: {
    icon: <AlertTriangle size={20} />,
    bgClass: 'bg-warning/10',
    borderClass: 'border-warning',
    iconClass: 'text-warning',
  },
  info: {
    icon: <Info size={20} />,
    bgClass: 'bg-info/10',
    borderClass: 'border-info',
    iconClass: 'text-info',
  },
};

const positionClasses: Record<ToastPosition, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

/**
 * Toast Component
 *
 * Individual toast notification with icon, message, and optional title.
 */
export function Toast({
  id,
  message,
  title,
  type = 'info',
  duration = 5000,
  icon,
  dismissible = true,
  onDismiss,
}: ToastProps) {
  const config = typeConfig[type];

  const handleDismiss = useCallback(() => {
    onDismiss?.(id);
  }, [id, onDismiss]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(handleDismiss, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [duration, handleDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      role="alert"
      aria-live="assertive"
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg',
        'bg-white min-w-[300px] max-w-[400px]',
        config.borderClass
      )}
    >
      <div className={cn('flex-shrink-0', config.iconClass)} aria-hidden="true">
        {icon || config.icon}
      </div>

      <div className="flex-1 min-w-0">
        {title && (
          <p className="text-body font-semibold text-warm-gray-dark mb-1">
            {title}
          </p>
        )}
        <p className="text-body-sm text-warm-gray">{message}</p>
      </div>

      {dismissible && (
        <button
          onClick={handleDismiss}
          className={cn(
            'flex-shrink-0 p-1 rounded-md',
            'text-warm-gray-light hover:text-warm-gray',
            'transition-colors duration-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2'
          )}
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      )}
    </motion.div>
  );
}

/**
 * ToastContainer Component
 *
 * Container that positions and animates toasts on the screen.
 *
 * @example
 * ```tsx
 * const [toasts, setToasts] = useState<ToastProps[]>([]);
 *
 * const addToast = (toast: Omit<ToastProps, 'id'>) => {
 *   setToasts(prev => [...prev, { ...toast, id: Date.now().toString() }]);
 * };
 *
 * const removeToast = (id: string) => {
 *   setToasts(prev => prev.filter(t => t.id !== id));
 * };
 *
 * <ToastContainer
 *   position="top-right"
 *   toasts={toasts}
 *   onDismiss={removeToast}
 * />
 * ```
 */
export function ToastContainer({
  position = 'top-right',
  toasts,
  onDismiss,
}: ToastContainerProps) {
  return (
    <div
      className={cn(
        'fixed z-50 flex flex-col gap-3',
        positionClasses[position]
      )}
      aria-label="Notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}

export default ToastContainer;
